<template>
  <div class="chat-page">
    <van-nav-bar fixed title="联系客服" left-text="返回" left-arrow @click-left="goBack" />

    <!-- 未登录 -->
    <div v-if="!isLogin" class="not-login">
      <van-icon name="service-o" size="60" color="#ff6b35" />
      <p>请先登录后再联系客服</p>
      <van-button type="primary" round @click="$router.push('/login')">去登录</van-button>
    </div>

    <template v-else>
      <!-- 连接中 -->
      <div v-if="!session" class="loading">
        <van-loading color="#ff6b35" />
        <p>正在连接客服...</p>
      </div>

      <!-- 聊天窗口 -->
      <div v-else class="chat-body">
        <div class="message-list" ref="messageList">
          <div v-if="!messages.length" class="empty-tip">
            您好，我是欢乐购商城客服，请问有什么可以帮您？
          </div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="message-item"
            :class="msg.sender_type === 1 ? 'mine' : 'agent'"
          >
            <img class="avatar" :src="msg.sender_type === 1 ? userAvatar : agentAvatar" alt="头像" @error="onAvatarError" />
            <div class="msg-col">
              <!-- 图片消息：默认显示缩略图，点击查看原图 -->
              <div v-if="msg.msg_type === 2" class="bubble bubble-img" @click="previewImage(msg.content)">
                <img :src="thumbUrl(msg.content)" alt="图片" loading="lazy" @error="onThumbError(msg.content)" />
              </div>
              <!-- 文本消息 -->
              <div v-else class="bubble">{{ msg.content }}</div>
              <!-- 已读回执：我发出的消息显示「客服是否已读」 -->
              <span
                v-if="msg.sender_type === 1"
                class="read-state"
                :class="{ read: isAgentRead(msg) }"
              >{{ isAgentRead(msg) ? '已读' : '未读' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入区 -->
      <div v-if="session" class="chat-input">
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none;"
          @change="onFileChange"
        />
        <van-icon name="photograph" class="img-btn" @click="$refs.fileInput.click()" />
        <input
          class="inp"
          type="text"
          v-model.trim="inputText"
          maxlength="500"
          placeholder="请输入您要咨询的内容..."
          @keyup.enter="send"
        >
        <button class="send-btn" @click="send">发送</button>
      </div>
    </template>

    <!-- 图片查看器：支持双击 / 双指捏合 / 滚轮缩放，拖动查看细节，长图默认按宽度显示 -->
    <ImageViewer
      v-model="previewVisible"
      :url="previewUrl"
      :thumb="previewThumb"
    />
  </div>
</template>

<script>
import { createChatSessionApi, sendChatMessageApi, getChatMessagesApi, uploadChatImageApi } from '@/api/chat'
import { getChatMessageCache, setChatMessageCache, setChatNotifiedMsgId } from '@/utils/chat'
import chatSocket from '@/utils/ws'
import { Toast } from 'vant'
import ImageViewer from '@/components/ImageViewer.vue'

// 客服默认头像（客服真实头像统一存放在服务器 /uploads/service_avatar/ 下）
const SERVICE_AVATAR = '/uploads/service_avatar/customer_service_avatar.png'
// 本地兜底头像（客服头像加载失败 / 用户未设置头像时使用）
const DEFAULT_AVATAR = require('@/assets/default-avatar.svg')

export default {
  name: 'Chat',
  components: { ImageViewer },
  data () {
    return {
      session: null,
      messages: [],
      lastId: 0,
      inputText: '',
      pollTimer: null,
      sending: false,
      // 图片查看器（自定义放大查看）
      previewVisible: false,
      previewUrl: '',
      previewThumb: '',
      // 客服已读游标：消息 id <= 该值表示客服已读（用于“客服已读我”回执）
      agentReadId: 0,
      // 缩略图加载失败的原图 URL 集合（用于回退显示原图）
      failedThumbs: {},
      // WebSocket 是否已连接（连接成功后不再依赖 HTTP 轮询）
      wsReady: false,
      // 等待 WebSocket 回执的消息：clientMsgId -> resolve
      pendingAcks: {},
      agentAvatar: SERVICE_AVATAR,
      userAvatar: DEFAULT_AVATAR,
      serviceAvatar: SERVICE_AVATAR,
      defaultAvatar: DEFAULT_AVATAR
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    }
  },
  watch: {
    // 登录失效后立即停止轮询并复位，避免持续请求失败接口
    isLogin (val) {
      if (!val) {
        this.stopPolling()
        this.unbindSocket()
        chatSocket.disconnect()
        this.session = null
        this.messages = []
      }
    }
  },
  created () {
    if (this.isLogin) {
      this.initChat()
    }
  },
  beforeDestroy () {
    this.stopPolling()
    this.unbindSocket()
  },
  methods: {
    // 返回（无历史记录时回到个人中心）
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/user')
      }
    },
    // 已读回执：该消息是否已被客服阅读（消息 id <= 客服已读游标）
    isAgentRead (msg) {
      return Number(msg && msg.id) > 0 && Number(msg.id) <= Number(this.agentReadId)
    },
    async initChat () {
      try {
        const { data } = await createChatSessionApi()
        this.session = data.session
        // 服务端返回客服已读游标，用于“客服已读我”回执
        this.agentReadId = Number(data.session && data.session.agent_last_read_id) || 0
        if (data.user && data.user.avatar) {
          this.userAvatar = data.user.avatar
        }
        // 复用内存缓存：避免每次进入聊天页都重新加载全部历史消息
        const cache = getChatMessageCache()
        if (cache.sessionId === this.session.id && cache.messages.length) {
          this.messages = cache.messages.slice()
          this.lastId = cache.lastId
        } else {
          this.lastId = 0
        }
        // 增量拉取新消息（首次进入为全量历史）
        await this.loadMessages()
        // 建立 WebSocket 实时通道（不可用时自动降级为 HTTP 轮询）
        this.bindSocket()
        chatSocket.connect('user')
        this.startPolling()
      } catch (err) {
        Toast(typeof err === 'string' ? err : '连接客服失败')
      }
    },

    // ==================== WebSocket 实时通道 ====================
    bindSocket () {
      this.unbindSocket()
      this._onWsOpen = () => {
        this.wsReady = true
        // 重连成功后补拉一次，避免断线期间漏掉的消息
        this.loadMessages().catch(() => {})
      }
      this._onWsMessage = (payload) => {
        if (!this.session || Number(payload.sessionId) !== Number(this.session.id)) return
        this.appendMessages([payload.message])
        if (payload.session && payload.session.agent_last_read_id) {
          const cursor = Number(payload.session.agent_last_read_id) || 0
          if (cursor > this.agentReadId) this.agentReadId = cursor
        }
        // 收到客服消息即上报已读，客服端可实时看到「买家已读」
        chatSocket.sendRead(this.session.id, this.lastId)
      }
      // 自己发出的消息：服务端入库成功后回执真实消息 id
      this._onWsAck = (payload) => {
        if (payload.clientMsgId && this.pendingAcks[payload.clientMsgId]) {
          this.pendingAcks[payload.clientMsgId](payload.message)
          delete this.pendingAcks[payload.clientMsgId]
        }
      }
      this._onWsRead = (payload) => {
        if (!this.session || Number(payload.sessionId) !== Number(this.session.id)) return
        const cursor = Number(payload.agent_last_read_id) || 0
        if (cursor > this.agentReadId) this.agentReadId = cursor
      }
      this._onWsClose = () => { this.wsReady = false }
      this._onWsError = () => { this.wsReady = false }

      chatSocket.on('open', this._onWsOpen)
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('ack', this._onWsAck)
      chatSocket.on('read', this._onWsRead)
      chatSocket.on('close', this._onWsClose)
      chatSocket.on('error', this._onWsError)
      this.wsReady = chatSocket.isOpen
    },
    unbindSocket () {
      if (this._onWsOpen) chatSocket.off('open', this._onWsOpen)
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsAck) chatSocket.off('ack', this._onWsAck)
      if (this._onWsRead) chatSocket.off('read', this._onWsRead)
      if (this._onWsClose) chatSocket.off('close', this._onWsClose)
      if (this._onWsError) chatSocket.off('error', this._onWsError)
      this._onWsOpen = null
      this._onWsMessage = null
      this._onWsAck = null
      this._onWsRead = null
      this._onWsClose = null
      this._onWsError = null
    },

    // 轮询仅作为 WebSocket 不可用时的兜底方案
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (chatSocket.isOpen) return
        this.loadMessages().catch(() => {})
      }, 3000)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    // 拉取新消息（markRead=1：聊天页内拉取即推进“买家已读游标”，供客服端显示已读回执）
    async loadMessages () {
      if (!this.session) return
      const { data } = await getChatMessagesApi(this.session.id, this.lastId, 1)
      // 同步客服已读游标：客服看过我的消息后，我的消息气泡显示「已读」
      const agentRead = Number(data.session && data.session.agent_last_read_id) || 0
      if (agentRead > this.agentReadId) {
        this.agentReadId = agentRead
      }
      if (data.messages && data.messages.length) {
        this.appendMessages(data.messages)
      }
      // 无论本次是否拉到新消息，都滚动到最底部显示最新消息
      this.$nextTick(() => this.scrollToBottom())
    },
    // 追加消息（自动按 id 去重、同步已读位与内存缓存）
    appendMessages (list) {
      if (!list || !list.length) return
      let added = false
      list.forEach(msg => {
        if (!msg || !msg.id) return
        if (this.messages.some(m => Number(m.id) === Number(msg.id))) return
        this.messages.push(msg)
        if (Number(msg.id) > this.lastId) this.lastId = Number(msg.id)
        added = true
      })
      if (!added) return
      // 同步「新消息提示」的已读位置，避免离开聊天页后对已读消息重复提示
      setChatNotifiedMsgId(this.lastId)
      // 更新内存缓存，避免再次进入聊天页重复加载历史
      if (this.session) setChatMessageCache(this.session.id, this.messages, this.lastId)
      this.$nextTick(() => this.scrollToBottom())
    },
    // 头像加载失败时回退到本地默认头像
    onAvatarError () {
      if (this.agentAvatar !== this.defaultAvatar) this.agentAvatar = this.defaultAvatar
      if (this.userAvatar !== this.defaultAvatar) this.userAvatar = this.defaultAvatar
    },
    // 等待 WebSocket 消息回执（超时返回 null，调用方走 HTTP 兜底）
    waitForAck (clientMsgId, timeout = 3000) {
      return new Promise(resolve => {
        const timer = setTimeout(() => {
          delete this.pendingAcks[clientMsgId]
          resolve(null)
        }, timeout)
        this.pendingAcks[clientMsgId] = (message) => {
          clearTimeout(timer)
          resolve(message || null)
        }
      })
    },
    scrollToBottom () {
      const el = this.$refs.messageList
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },
    // 发送文本消息
    async send () {
      if (!this.inputText) {
        Toast('请输入内容')
        return
      }
      const content = this.inputText
      const ok = await this.sendMsg(content, 1)
      if (ok) this.inputText = ''
    },
    // 发送消息（msgType: 1=文本 2=图片）
    // 优先走 WebSocket；WebSocket 不可用（或回执超时）时自动降级为 HTTP 接口，保证消息不丢
    async sendMsg (content, msgType) {
      if (!content) return false
      if (this.sending) return false
      this.sending = true
      try {
        const clientMsgId = 'c' + Date.now() + Math.random().toString(16).slice(2)
        const viaWs = chatSocket.isOpen &&
          chatSocket.sendChatMessage(this.session.id, content, msgType, clientMsgId)
        if (viaWs) {
          const ackMsg = await this.waitForAck(clientMsgId)
          if (ackMsg) {
            this.appendMessages([ackMsg])
            return true
          }
          // 已通过 WebSocket 发出但未收到回执：不做 HTTP 重发（避免产生重复消息），
          // 改为重新拉取一次消息列表进行校准
          await this.loadMessages().catch(() => {})
          return true
        }
        // HTTP 兜底（WebSocket 未连接时）
        const { data } = await sendChatMessageApi(this.session.id, content, msgType)
        this.appendMessages([data])
        return true
      } catch (err) {
        Toast(typeof err === 'string' ? err : '发送失败，请稍后重试')
        return false
      } finally {
        this.sending = false
      }
    },
    // 选择图片后上传并发送
    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        Toast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        Toast('图片大小不能超过 5MB')
        return
      }
      try {
        const { data } = await uploadChatImageApi(file)
        await this.sendMsg(data.url, 2)
      } catch (err) {
        Toast(typeof err === 'string' ? err : '图片上传失败，请重试')
      }
    },
    // 打开图片查看器（支持双击 / 双指捏合 / 滚轮缩放，拖动查看细节）
    previewImage (url) {
      if (!url) return
      // 缩略图作为加载占位（缩略图不存在时留空，直接加载原图）
      this.previewThumb = this.failedThumbs[url] ? '' : this.thumbUrl(url)
      this.previewUrl = url
      this.previewVisible = true
    },
    // 生成缩略图地址（服务器上传时生成 xxx_thumb.jpg）
    thumbUrl (url) {
      if (!url || this.failedThumbs[url]) return url
      // 已上传时生成的缩略图为 jpg：xxx_thumb.jpg
      const idx = url.lastIndexOf('.')
      if (idx <= url.lastIndexOf('/')) return url
      return url.slice(0, idx) + '_thumb.jpg'
    },
    // 缩略图加载失败（旧图片没有缩略图）时回退显示原图
    onThumbError (url) {
      if (url && !this.failedThumbs[url]) {
        this.$set(this.failedThumbs, url, true)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.chat-page {
  height: 100vh;
  height: 100dvh; /* 兼容移动端浏览器地址栏收起/展开 */
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding-top: 46px;
  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }

  .not-login {
    text-align: center;
    padding: 80px 20px;

    p {
      color: #666;
      margin: 16px 0 24px;
      font-size: 15px;
    }
  }

  .loading {
    text-align: center;
    padding: 80px 20px;

    p {
      color: #999;
      margin-top: 16px;
      font-size: 14px;
    }
  }

  .chat-body {
    flex: 1;
    overflow: hidden;

    .message-list {
      height: 100%;
      overflow-y: auto;
      padding: 16px 12px calc(54px + env(safe-area-inset-bottom));
      box-sizing: border-box;

      .empty-tip {
        text-align: center;
        color: #999;
        font-size: 13px;
        padding: 40px 20px;
        background: #fff;
        border-radius: 8px;
      }

      .message-item {
        display: flex;
        align-items: flex-end;
        margin-bottom: 16px;

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          background: #fff;
          object-fit: cover;
        }

        .msg-col {
          display: flex;
          flex-direction: column;
          max-width: 65%;
          min-width: 0;

          .bubble {
            max-width: 100%;
            padding: 10px 12px;
            border-radius: 10px;
            font-size: 15px;
            line-height: 22px;
            word-break: break-all;
            white-space: pre-wrap;
          }

          // 图片消息气泡
          .bubble-img {
            padding: 4px;
            background: #fff;
            line-height: 0;
            cursor: pointer;
            border-radius: 10px;

            img {
              display: block;
              max-width: 160px;
              max-height: 160px;
              border-radius: 6px;
              object-fit: cover;
            }
          }

          // 已读回执（我发出的消息：客服是否已读）
          .read-state {
            align-self: flex-end;
            font-size: 11px;
            line-height: 1.3;
            color: #c9c9cc;
            margin-top: 3px;
            padding-right: 4px;

            &.read {
              color: #07c160;
            }
          }
        }

        &.agent {
          justify-content: flex-start;

          .msg-col {
            margin-left: 8px;
            align-items: flex-start;
          }

          .bubble {
            background: #fff;
            color: #333;
            border-top-left-radius: 2px;
          }
        }

        &.mine {
          justify-content: flex-end;

          .avatar {
            order: 2;
            margin-left: 8px;
          }

          .msg-col {
            align-items: flex-end;
          }

          .bubble {
            background: linear-gradient(90deg, #ecb53c, #ff9211);
            color: #fff;
            border-top-right-radius: 2px;
          }
        }
      }
    }
  }

  .chat-input {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #eee;

    .inp {
      flex: 1;
      height: 36px;
      border: none;
      outline: none;
      background: #f5f5f5;
      border-radius: 18px;
      padding: 0 14px;
      font-size: 14px;
    }

    .img-btn {
      font-size: 24px;
      color: #ff9211;
      margin-right: 10px;
      flex-shrink: 0;
    }

    .send-btn {
      width: 64px;
      height: 36px;
      margin-left: 10px;
      border: none;
      border-radius: 18px;
      background: linear-gradient(90deg, #ecb53c, #ff9211);
      color: #fff;
      font-size: 14px;
    }
  }

  // 图片查看器见 components/ImageViewer.vue（全屏遮罩 + 缩放 / 拖动 / 适应宽度）

}
</style>